/**
 * useNavAlerts — aggregates the handful of "there's something to review" signals
 * into a { notifKey: count } map consumed by the mega-menu (roll-up dots on
 * domains, count badges on rows, total on the bell).
 *
 * v1 wires only the sources that already have endpoints, and only fetches the
 * ones the current user is permitted to see (so we don't spray 403s). Each fetch
 * is independent and failure-swallowing — one dead endpoint never blanks the rest.
 *
 * Deferred (need new backend aggregates): firmware-out-of-date, orders-awaiting-
 * fulfillment. The receipt-reconciliation module is wired below via
 * `/receipts/review-count` (the `receipts` notif key) — same dot, no new mechanism.
 *
 * Poll cadence mirrors the receipt-branch nav dot: fetch on mount + 60s backstop.
 */
import { useEffect, useState } from 'react';
import { useApi } from '../apiHelpers';
import useUserStore from '../stores/useUserStore';
import { userCanAccess } from '../navigation/menuConfig';
import { notifAllowedOnInstance } from '../navigation/navTree';

export type NavAlerts = Record<string, number>;

const can = (require: string | undefined, user: unknown): boolean =>
  userCanAccess({ require } as { require?: string }, user);

export function useNavAlerts(): NavAlerts {
  const api = useApi();
  const user = useUserStore((s) => s.user);
  const [alerts, setAlerts] = useState<NavAlerts>({});

  useEffect(() => {
    if (!user?.loggedin) {
      setAlerts({});
      return;
    }
    let cancelled = false;

    const load = async () => {
      const next: NavAlerts = {};
      const tasks: Promise<unknown>[] = [];

      // Shows → Monday inbox pending imports
      if (can('hasRole', user)) {
        tasks.push(
          api
            .get('/monday-inbox?status=pending', { silent: true })
            .then((rows: unknown) => {
              if (Array.isArray(rows) && rows.length) next.monday = rows.length;
            })
            .catch(() => {}),
        );
      }

      // Shows → gear that is out and due back today or earlier, i.e. a Return
      // click somebody owes on /dispatch. The `logistics` key has been declared on
      // the Dispatch nav row since the mega-menu shipped with nothing filling it.
      //
      // Counting everything in transit would be the `receiving` mistake below all
      // over again: gear is out somewhere all season, the badge would never go
      // dark, and a badge that is always lit is furniture. returnsDue is empty in
      // steady state, which is what earns it the right to interrupt.
      if (can('hasRole', user)) {
        tasks.push(
          api
            .get('/events/logistics-stats', { silent: true })
            .then((s: { returnsDue?: number } | null) => {
              if (s?.returnsDue) next.logistics = s.returnsDue;
            })
            .catch(() => {}),
        );
      }

      // Sales → open Drone Transfer decisions (pilots marked drones for a
      // customer; Sales still owes a lease/sale call). Same endpoint the
      // Transfers page loads; length = pending count.
      if (can('sales', user)) {
        tasks.push(
          api
            .get('/assignment-requests', { silent: true })
            .then((rows: unknown) => {
              if (Array.isArray(rows) && rows.length) next.transfers = rows.length;
            })
            .catch(() => {}),
        );
      }

      // Service → RMA pending review + awaiting receipt
      tasks.push(
        api
          .get('/rma/stats', { silent: true })
          .then((s: { pending_review?: number; awaiting_receipt?: number } | null) => {
            const c = (s?.pending_review || 0) + (s?.awaiting_receipt || 0);
            if (c > 0) next.rma = c;
          })
          .catch(() => {}),
      );

      // Tools → my (or handed-to-me) receipts awaiting review. No gate: everyone
      // has their own receipts; the endpoint scopes to the caller (effective owner).
      tasks.push(
        api
          .get('/receipts/review-count', { silent: true })
          .then((d: { count?: number } | null) => {
            if (d?.count) next.receipts = d.count;
          })
          .catch(() => {}),
      );

      // Tools → requests waiting on the caller: approvals for the approver list,
      // ready-to-buy for the buyer list. No gate: anyone may be on either list,
      // and the server answers 0 for a role the caller lacks. Two bell lines
      // deep-link to two tabs; the nav row carries their sum.
      tasks.push(
        api
          .get('/requisitions/me', { silent: true })
          .then((d: { counts?: { pending?: number; to_buy?: number } } | null) => {
            const pending = d?.counts?.pending || 0;
            const toBuy = d?.counts?.to_buy || 0;
            if (pending) next.requisitionApprovals = pending;
            if (toBuy) next.requisitionsToBuy = toBuy;
            if (pending + toBuy) next.requisitions = pending + toBuy;
          })
          .catch(() => {}),
      );

      // Warehouse → purchase orders waiting on the CALLER's approval. The server
      // answers 0 unless the caller is an approver (named list, or the admin
      // bootstrap), so this cannot become the permanently-lit badge `receiving`
      // was: for everyone else it is silent, and for an approver it is exactly
      // the Slack DM's information with a second, in-app path to it.
      if (can('procurement', user)) {
        tasks.push(
          api
            .get('/procurement/approval-inbox-count', { silent: true })
            .then((d: { count?: number } | null) => {
              if (d?.count) next.approvals = d.count;
            })
            .catch(() => {}),
        );
      }

      // Warehouse → work orders a confirmed sale suggested, waiting for a
      // manager. Empty in steady state: a suggestion is approved or dismissed.
      // Only an approver is badged: everyone else can read the inbox but not act on it.
      if (can('workOrders', user) && (user?.isadmin || user?.can_admin || user?.can_manager)) {
        tasks.push(
          api
            .get('/work-orders/inbox', { silent: true })
            .then((rows: unknown) => {
              if (Array.isArray(rows) && rows.length) next.workOrderInbox = rows.length;
            })
            .catch(() => {}),
        );
      }

      // Warehouse → runs on the floor where a station asked for a box. Only the
      // people who can pick are badged. Counts runs, not requests.
      if (can('workOrders', user) && (user?.isadmin || user?.can_admin || user?.can_manufacturing)) {
        tasks.push(
          api
            .get('/work-orders/floor', { silent: true })
            .then((rows: unknown) => {
              const c = Array.isArray(rows)
                ? rows.filter((r: { open_pick_requests?: number }) => (r?.open_pick_requests ?? 0) > 0).length
                : 0;
              if (c) next.workOrderPicks = c;
            })
            .catch(() => {}),
        );
      }

      if (can('receivingHub', user)) {
        // No `receiving` alert. A pending/partial expected-receipt is the normal
        // resting state of an open purchase order, not something needing
        // attention, so the badge was permanently lit and taught people to ignore
        // the bell. Removed for BOTH instances 2026-07-25. `lowparts` below is a
        // real threshold breach and stays.

        // Warehouse → parts the reorder report lists as short
        tasks.push(
          api
            .get('/dashboard/parts-status', { silent: true })
            .then((d: { rows?: Array<{ is_short?: boolean }> } | null) => {
              // The server marks short from the reorder report; never re-judge it here.
              const c = (d?.rows || []).filter((r) => r.is_short).length;
              if (c > 0) next.lowparts = c;
            })
            .catch(() => {}),
        );
      }

      await Promise.all(tasks);
      if (next.workOrderInbox) next.workOrders = next.workOrderInbox;
      if (!cancelled) {
        // Only update state when a count actually changed — most 60s polls
        // return identical counts, and a fresh object would re-render the
        // whole NavBar subtree for nothing.
        setAlerts((prev) => {
          // Drop counts for features this deployment does not have, so the
          // roll-up bell cannot badge work that has nowhere to be shown.
          Object.keys(next).forEach((k) => { if (!notifAllowedOnInstance(k)) delete next[k]; });
          const keys = Object.keys(next);
          const unchanged = keys.length === Object.keys(prev).length && keys.every((k) => prev[k] === next[k]);
          return unchanged ? prev : next;
        });
      }
    };

    load();
    const timer = setInterval(load, 60000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
    // `api` (useApi) is a fresh literal each render and is intentionally excluded
    // to avoid a refetch loop; we refetch when the user (identity/capabilities) changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return alerts;
}
